<template>
  <div class="popular-chats-container">
    <div class="section-header">
      <h2 class="headline">
        <v-icon left color="primary">mdi-cast-education</v-icon>
        Featured Conversations
      </h2>
      <p class="subtitle-1">Explore what users have created using UnStruct.AI</p>
    </div>

    <div v-if="loading" class="loading-container">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <span class="ml-3">Loading featured conversations...</span>
    </div>

    <div v-else-if="popularChats.length === 0" class="no-results">
      <v-icon size="64" color="grey lighten-1">mdi-podcast</v-icon>
      <p class="mt-4 grey--text">No featured conversations found yet!</p>
    </div>

    <v-row v-else class="chat-grid">
      <v-col
        v-for="chat in popularChats"
        :key="chat.share_id"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <v-hover v-slot="{ hover }">
          <v-card
            class="chat-card"
            :elevation="hover ? 8 : 2"
            :class="{ 'on-hover': hover }"
            @click="navigateToChat(chat.share_id)"
          >
            <v-card-text>
              <div class="d-flex align-center mb-2">
                <v-avatar :color="getRandomColor()" size="32" class="mr-2">
                  <v-icon dark>{{ getRandomIcon() }}</v-icon>
                </v-avatar>
                <div class="chat-stats">
                  <div class="views">
                    <v-icon x-small color="grey">mdi-eye</v-icon>
                    <span class="caption">{{ formatViewCount(chat.share_view_count) }}</span>
                  </div>
                  <div class="timestamp">
                    <v-icon x-small color="grey">mdi-clock-outline</v-icon>
                    <span class="caption">{{ formatDate(chat.created_at) }}</span>
                  </div>
                </div>
              </div>

              <h3 class="chat-title">{{ truncateText(chat.user_message, 100) }}</h3>

              <div class="chat-actions">
                <v-btn
                  small
                  text
                  color="primary"
                  class="play-btn"
                >
                  <v-icon left>mdi-play-circle-outline</v-icon>
                  Play Podcast
                </v-btn>

                <v-btn
                  icon
                  small
                  @click.stop="shareChat(chat.share_id)"
                >
                  <v-icon color="grey lighten-1">mdi-share-variant</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-hover>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: 'PopularChats',

  props: {
    popularChats: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    navigateToChat(shareId) {
      if (shareId) {
        const host = window.location.host;
        const organization = this.$store.state.auth.currentOrganization;
        const url = `https://${host}/${organization}/chat?chat_id=${shareId}`;
        window.location.href = url;
      }
    },

    shareChat(shareId) {
      // Use your existing share functionality
      const host = window.location.host;
      const organization = this.$store.state.auth.currentOrganization;
      const shareUrl = `https://${host}/${organization}/chat?chat_id=${shareId}`;
      navigator.clipboard.writeText(shareUrl);
      this.$store.dispatch('websocket/showTempMessageFn', {
        message: 'Copied shareable link to clipboard',
        type: 'info',
        timeout: 2000
      });
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    formatViewCount(count) {
      if (!count) return '0 views';
      if (count === 1) return '1 view';
      return `${count} views`;
    },

    formatDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else if (diffDays < 30) {
        return `${Math.floor(diffDays / 7)} weeks ago`;
      } else if (diffDays < 365) {
        return `${Math.floor(diffDays / 30)} months ago`;
      } else {
        return `${Math.floor(diffDays / 365)} years ago`;
      }
    },

    getRandomColor() {
      const colors = [
        'indigo darken-2',
        'deep-purple darken-2',
        'blue darken-3',
        'teal darken-2',
        'green darken-3',
        'orange darken-3',
        'deep-orange darken-2',
        'brown darken-2',
        'blue-grey darken-2',
        'red darken-2',
        'pink darken-2'
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },

    getRandomIcon() {
      const icons = [
        'mdi-podcast',
        'mdi-microphone-variant',
        'mdi-account-voice',
        'mdi-text-box-outline',
        'mdi-forum-outline',
        'mdi-chart-bubble',
        'mdi-thought-bubble-outline',
        'mdi-message-text-outline',
        'mdi-comment-quote-outline'
      ];
      return icons[Math.floor(Math.random() * icons.length)];
    }
  }
}
</script>

<style scoped>
.popular-chats-container {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.headline {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-weight: 500;
}

.subtitle-1 {
  color: rgba(0, 0, 0, 0.6);
}

.theme--dark .subtitle-1 {
  color: rgba(255, 255, 255, 0.7);
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.no-results {
  text-align: center;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.chat-card {
  height: 100%;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
}

.chat-card.on-hover {
  transform: translateY(-5px);
}

.theme--dark .chat-card {
  background: rgba(48, 48, 48, 0.95);
}

.chat-stats {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

.views, .timestamp {
  display: flex;
  align-items: center;
  gap: 4px;
}

.chat-title {
  font-size: 1rem;
  line-height: 1.4;
  margin-bottom: 16px;
  min-height: 66px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.chat-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
}

.play-btn {
  letter-spacing: 0;
  transition: all 0.3s ease;
}

.play-btn:hover {
  transform: scale(1.05);
}

@media (max-width: 768px) {
  .popular-chats-container {
    padding: 16px;
    padding-bottom: 80px;
  }

  .section-header {
    margin-bottom: 24px;
  }

  .chat-grid {
    margin: 0;
  }
}
</style>
